import React from 'react';
import styled from 'styled-components';
import logoMark from '../images/logo-mark.png';
import { ink, wordmarkFont } from '../theme/palette';

/**
 * The mark's own pixel dimensions, published so every `<img>` that renders it
 * can declare an intrinsic size.
 *
 * Without these the browser has no aspect ratio to work from until the file
 * arrives, so a `width: auto` mark occupies zero width and everything beside it
 * jumps when it lands — the layout shift Lighthouse flags as "unsized image
 * element". Re-export the real numbers whenever the artwork is swapped;
 * `object-fit: contain` keeps a stale ratio from distorting the new file.
 */
export const MARK_INTRINSIC_WIDTH = 256;
export const MARK_INTRINSIC_HEIGHT = 256;

interface LogoProps {
  /** Wordmark colour. Defaults to ink; the dark footer passes white. */
  fill?: string;
  width?: number;
  height?: number;
  /** Overrides the wordmark size, which otherwise tracks the mark's height. */
  wordmarkSize?: number;
}

/**
 * Keep the mark and wordmark centred as a single horizontal lockup. The mark's
 * circular frame gives both elements a reliable visual midpoint.
 */
const LogoContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 560px) {
    gap: 8px;
  }
`

/**
 * Height is fixed; width comes from the file's own proportions.
 *
 * This deliberately avoids hardcoding an aspect ratio. The mark has been
 * swapped several times — bare bucket, circular badge, bucket again — and each
 * export has different proportions, so a baked-in ratio silently stretches
 * whatever lands here next. `width: auto` can never squash the artwork.
 */
const Mark = styled.img<{ $height: number }>`
  display: block;
  height: ${props => props.$height}px;
  width: auto;
  object-fit: contain;

  @media (max-width: 560px) {
    height: ${props => Math.round(props.$height * 0.72)}px;
  }
`;

// Spacing and vertical placement are the container's job now, so this carries
// type only — no margin nudges to re-tune when the mark is swapped again.
//
// The lockup shrinks as one on phones: mark and wordmark step down by roughly
// the same ratio so their optical balance survives the change.
const BrandName = styled.span<{ $fill: string; $size: number }>`
  font: 900 ${props => props.$size}px / 1 ${wordmarkFont};
  letter-spacing: -0.02em;
  color: ${props => props.$fill};

  @media (max-width: 560px) {
    font-size: ${props => Math.round(props.$size * 0.74)}px;
  }
`;

const Logo: React.FC<LogoProps> = ({
  fill = ink,
  height = 52,
  wordmarkSize,
}) => {
  return (
    <LogoContainer>
      <Mark
        src={logoMark}
        alt=""
        $height={height}
        width={MARK_INTRINSIC_WIDTH}
        height={MARK_INTRINSIC_HEIGHT}
        aria-hidden="true"
      />
      {/* The wordmark tracks the mark's height so the lockup scales as one —
          the dark footer renders it small and in white. */}
      <BrandName $fill={fill} $size={wordmarkSize ?? Math.round(height * 0.65)}>
        Poplearn
      </BrandName>
    </LogoContainer>
  );
};

export default Logo;
